import { useCallback, useEffect, useMemo } from "react";

import { useGetUndownloadedFolderReportsLazyQuery } from "@/graphql/common/common.hooks";
import { authService } from "@/services/authService";
import { GraphqlMappedError } from "@/services/graphqlErrorService";

const CROSS_TAB_CHANNEL_NAME = "undownloaded-folder-reports";

// One channel per tab, shared by every hook instance — not per-component, so we
// don't open a new BroadcastChannel per mount.
const channel =
  typeof BroadcastChannel === "undefined"
    ? null
    : new BroadcastChannel(CROSS_TAB_CHANNEL_NAME);

// Call after a local fetch reveals a real change so other open tabs refetch too.
function notifyOtherTabs() {
  channel?.postMessage("refresh");
}

function useUndownloadedFolderReports() {
  const [fetchUndownloadedFolderReports, { loading, error }] =
    useGetUndownloadedFolderReportsLazyQuery({
      // "no-cache" keeps this query's result out of the normalized cache entity
      // shared with GetAuth (same UserType:<id>) — writing there would make
      // Shell's GetAuth useQuery see new data and re-run authService.setUser.
      fetchPolicy: "no-cache",
    });

  // Fetches and applies the result locally, reporting back whether anything actually
  // changed. Deliberately does NOT decide whether to notify other tabs — the broadcast
  // listener below reuses this same fetch/apply logic but must never notify off the
  // back of it, or tab B (updating because tab A told it to) would re-broadcast and
  // tab A would refetch again, forever ping-ponging between tabs.
  const fetchAndApply = useCallback(async () => {
    const { data } = await fetchUndownloadedFolderReports();
    if (!data?.user) return false;

    const previous = authService.getUser().undownloadedFolderReports;
    const next = data.user.undownloadedFolderReports;
    authService.setUndownloadedFolderReports(next);

    return JSON.stringify(previous) !== JSON.stringify(next);
  }, [fetchUndownloadedFolderReports]);

  // Kept stable across the loading/error toggles a fetch causes — callers pass this into
  // useEffect deps, and a reference that churns every fetch would re-trigger that effect
  // and refetch forever.
  const refreshUndownloadedFolderReports = useCallback(async () => {
    const changed = await fetchAndApply();
    if (changed) notifyOtherTabs();
  }, [fetchAndApply]);

  useEffect(() => {
    if (!channel) return;

    // Only applies the fetched data locally — never notifies. Every tab that needed to
    // know about the change already got the original broadcast; re-posting here would
    // just bounce back and forth between tabs.
    const handleMessage = () => {
      fetchAndApply();
    };

    channel.addEventListener("message", handleMessage);
    return () => channel.removeEventListener("message", handleMessage);
  }, [fetchAndApply]);

  const result = useMemo(
    () => ({
      refreshUndownloadedFolderReports,
      refreshUndownloadedFolderReportsLoading: loading,
      refreshUndownloadedFolderReportsError:
        error && GraphqlMappedError.from(error),
    }),
    [refreshUndownloadedFolderReports, loading, error]
  );

  return result;
}

export default useUndownloadedFolderReports;
